import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { IoChevronBackOutline } from "react-icons/io5";
import { generateLeaderboardUrl, copyToClipboard } from "../Utilities/shareUtils";
import TeamTable from "./TeamTable";
import { supabase } from "../ReusableComponents/supabaseClient";
import StatusBadge from "../ReusableComponents/StatusBadge";
import PoolRulesPopover from "../ReusableComponents/PoolRulesPopover";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useLeaderboardData, fetchPoolDetailsById } from "./LeaderboardHelper";
import moment from "moment-timezone";
import { logError } from "../Utilities/errorLogger";
import { useAuth } from "../Utilities/AuthContext";

function Leaderboards() {
  const { poolId } = useParams();
  const navigate = useNavigate();
  const [poolData, setPoolData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const { teams, teamLineItems, loading: leaderboardLoading } = useLeaderboardData(poolId);
  const { ownerData } = useAuth();
  const [userId] = useState(Number(ownerData?.userId || localStorage.getItem("userId")));

  const isTournamentStarted = useMemo(() => {
    if (!poolData) return false;
    const timezone = poolData.time_zone || "UTC";
    const now = moment().tz(timezone);
    const firstTeeTime = poolData.first_tee_time
      ? moment.utc(poolData.first_tee_time).tz(timezone)
      : moment.utc(poolData.tournament_fixture?.start_date).tz(timezone).startOf("day").add(5, "hours");
    return now.isAfter(firstTeeTime) || (poolData.tournament_status && poolData.tournament_status !== "Not Started");
  }, [poolData]);

  const canViewTeam = useCallback(
    (team) => isTournamentStarted || team.owner === userId,
    [userId, isTournamentStarted]
  );

  useEffect(() => {
    setLoading(true);
    fetchPoolDetailsById(poolId)
      .then((data) => { setPoolData(data); setLoading(false); })
      .catch((err) => {
        logError({ filePath: "Leaderboard/Leaderboard.js", functionName: "fetchPoolDetails", errorType: "Fetch Error", errorMessage: err.message, stackTrace: err.stack, requestData: { poolId } });
        setError("Failed to fetch pool details");
        toast.error("Failed to fetch pool details", { theme: "dark" });
        setLoading(false);
      });

    const channel = supabase
      .channel("poolTableSubscription")
      .on("postgres_changes", { event: "UPDATE", schema: "public", table: "pool", filter: `id=eq.${poolId}` }, (payload) => { setPoolData(payload.new); })
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, [poolId]);

  if (loading || leaderboardLoading) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-6 md:pt-20">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-1/3" />
          <div className="grid grid-cols-4 gap-3">
            {[1, 2, 3, 4].map(i => <div key={i} className="h-16 bg-gray-200 rounded-xl" />)}
          </div>
          <div className="space-y-2">
            {[1, 2, 3].map(i => <div key={i} className="h-14 bg-gray-200 rounded-xl" />)}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-6 md:pt-20">
        <div className="bg-white rounded-2xl shadow-sm p-8 text-center text-red-600">{error}</div>
      </div>
    );
  }

  const stats = poolData ? [
    { label: "Tournament", value: <StatusBadge status={poolData.tournament_status || "Not Started"} /> },
    { label: "Round", value: <StatusBadge status={poolData.round_status || "Not Started"} /> },
    { label: "Current Round", value: poolData.current_round || "--" },
    { label: "Cut Line", value: poolData.cut_value || "--" },
  ] : [];

  return (
    <div className="max-w-4xl mx-auto px-4 py-6 md:pt-20 mb-24 md:mb-6">
      <ToastContainer position="top-center" theme="dark" />
      {/* Header */}
      <div className="bg-gradient-to-r from-primary to-primary-light rounded-2xl p-6 mb-6 shadow-lg">
        <div className="flex items-center gap-3 mb-4">
          <button onClick={() => navigate(-1)} className="text-green-200 hover:text-white transition-colors">
            <IoChevronBackOutline className="w-5 h-5" />
          </button>
          <h1 className="text-2xl font-bold text-white tracking-tight flex-1 min-w-0 truncate">{poolData?.name || "Leaderboard"}</h1>
          {poolData && (
            <PoolRulesPopover
              maxPlayers={poolData.max_players}
              numPlayersScored={poolData.num_player_used_for_scoring}
              allMustMakeCut={poolData.all_players_must_make_cut}
              tieBreaker={poolData.tie_breaker}
              onCopyLink={poolData.leaderboard_token ? async () => {
                const url = generateLeaderboardUrl(poolData.leaderboard_token);
                const success = await copyToClipboard(url);
                if (success) toast.success("Leaderboard link copied!", { theme: "dark", autoClose: 2000 });
              } : undefined}
            />
          )}
        </div>

        {/* Stats inside header */}
        {poolData && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 md:gap-3">
            {stats.map((stat) => (
              <div key={stat.label} className="bg-white/15 backdrop-blur-sm rounded-xl p-3 text-center">
                <p className="text-[10px] font-semibold text-green-200 uppercase tracking-wider">{stat.label}</p>
                <div className="mt-1 flex justify-center">
                  {typeof stat.value === "string" || typeof stat.value === "number"
                    ? <p className="text-sm font-bold text-white">{stat.value}</p>
                    : stat.value
                  }
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Team Table or Team List */}
      {teamLineItems.length > 0 ? (
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
          <TeamTable teams={teams} teamLineItems={teamLineItems} canViewTeam={canViewTeam} isTournamentStarted={isTournamentStarted} tieBreaker={poolData?.tie_breaker} />
        </div>
      ) : teams.length > 0 ? (
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="px-5 py-4 border-b border-gray-200 bg-gray-50/50">
            <h3 className="text-sm font-semibold text-gray-900">Registered Teams</h3>
            <p className="text-xs text-gray-500 mt-0.5">Scores will appear once the tournament begins</p>
          </div>
          <div className="p-3 space-y-2">
            {teams.map((team) => (
              <div key={team.id} className="flex items-center justify-between px-4 py-3.5 bg-gray-50 rounded-xl border border-gray-100">
                <div>
                  <p className="text-sm font-medium text-gray-900">{team.player_name || team.ownerDetails?.full_name || "Unknown"}</p>
                  <p className="text-xs text-gray-500 mt-0.5">
                    {team.line_items?.[0]?.count || 0}/{team.pool?.max_players || poolData?.max_players || "?"} players
                    {team.paid && <span className="ml-2 text-green-600 font-medium">Paid</span>}
                  </p>
                </div>
                {team.rank && (
                  <div className="w-9 h-9 rounded-full bg-white border border-gray-200 shadow-sm flex items-center justify-center">
                    <span className="text-sm font-bold text-gray-700">{team.rank}</span>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-12 text-center">
          <p className="text-gray-400 text-lg">No teams registered for this pool yet</p>
        </div>
      )}
    </div>
  );
}

export default Leaderboards;
